import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { DevicesClass, DeviceTypes } from '@app/core/classes';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import {
  PerformerApplicationVm,
  PerformerBookingApplicationStatus
} from '@app/modules/booking-modules/booking.model';
import { AccountService } from '@app/modules/account-modules/account.service';
import { getActiveBookingPerformerId } from '@app/modules/account-modules/account.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { AppStaticURL } from '@app/app.settings';
import { ConfirmationService } from 'primeng/api';
import { RoutingConfig } from '@app/routing/routing.config';

@UntilDestroy()
@Component({
  selector: 'app-performer-venue-applications',
  templateUrl: './performer-venue-applications.component.html',
  styleUrls: ['./performer-venue-applications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PerformerVenueApplicationsComponent extends DevicesClass implements OnInit {
  readonly DTypes = DeviceTypes;
  readonly StaticURL = AppStaticURL;
  readonly Routing = RoutingConfig;
  readonly pageSize = 20;
  readonly statusOptions = [
    { name: 'Все статусы', value: '' as const },
    { name: 'Ожидает', value: PerformerBookingApplicationStatus.Pending },
    { name: 'Принята', value: PerformerBookingApplicationStatus.Accepted },
    { name: 'Отклонена', value: PerformerBookingApplicationStatus.Declined },
    { name: 'Отозвана', value: PerformerBookingApplicationStatus.Withdrawn }
  ];

  performerId: string | null = null;
  loading = false;
  loadError: string | null = null;
  items: PerformerApplicationVm[] = [];
  page = 1;
  hasMore = false;
  from = '';
  to = '';
  status: PerformerBookingApplicationStatus | '' = '';
  actionLoadingId: string | null = null;

  constructor(
    injector: Injector,
    public cdr: ChangeDetectorRef,
    private bookingService: BookingService,
    private accountService: AccountService,
    private confirmationService: ConfirmationService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.resolvePerformerAndLoad();
  }

  onApplyFilters(): void {
    this.page = 1;
    this.loadApplications();
  }

  onResetFilters(): void {
    this.from = '';
    this.to = '';
    this.status = '';
    this.page = 1;
    this.loadApplications();
  }

  onPrevPage(): void {
    if (this.page <= 1 || this.loading) {
      return;
    }
    this.page -= 1;
    this.loadApplications();
  }

  onNextPage(): void {
    if (!this.hasMore || this.loading) {
      return;
    }
    this.page += 1;
    this.loadApplications();
  }

  venueName(item: PerformerApplicationVm): string {
    return item.customer?.name || 'Заведение';
  }

  venuePhoto(item: PerformerApplicationVm): string | null {
    const c = item.customer;
    if (!c) {
      return null;
    }
    const rel = c.image?.thumbnailPath || c.image?.path || c.picPath || c.avatarPath || null;
    if (!rel) {
      return null;
    }
    return /^https?:\/\//i.test(rel) ? rel : this.StaticURL + String(rel).replace(/^\//, '');
  }

  venueProfileLink(item: PerformerApplicationVm): string[] | null {
    const id = item.customer?.id;
    if (!id) {
      return null;
    }
    return ['/', RoutingConfig.VenuesList, id];
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  }

  statusLabel(status: PerformerBookingApplicationStatus | null | undefined): string {
    switch (status) {
      case PerformerBookingApplicationStatus.Pending:
        return 'Ожидает';
      case PerformerBookingApplicationStatus.Accepted:
        return 'Принята';
      case PerformerBookingApplicationStatus.Declined:
        return 'Отклонена';
      case PerformerBookingApplicationStatus.Withdrawn:
        return 'Отозвана';
      default:
        return status ? String(status) : '—';
    }
  }

  canWithdraw(item: PerformerApplicationVm): boolean {
    return item.status === PerformerBookingApplicationStatus.Pending;
  }

  withdraw(item: PerformerApplicationVm): void {
    const requestId = item.requestId;
    if (!this.performerId || !requestId || !this.canWithdraw(item) || this.actionLoadingId) {
      return;
    }
    this.confirmationService.confirm({
      message: 'Отозвать заявку в это заведение?',
      accept: () => {
        this.actionLoadingId = requestId;
        this.cdr.markForCheck();
        this.bookingService
          .withdrawPerformerApplication(this.performerId!, requestId)
          .pipe(
            finalize(() => {
              this.actionLoadingId = null;
              this.cdr.markForCheck();
            }),
            untilDestroyed(this)
          )
          .subscribe(
            () => this.loadApplications(),
            err => {
              this.loadError =
                (err && (err.message || err.error?.message)) || 'Не удалось отозвать заявку.';
              this.cdr.markForCheck();
            }
          );
      }
    });
  }

  private resolvePerformerAndLoad(): void {
    this.loading = true;
    this.loadError = null;
    this.cdr.markForCheck();
    this.accountService
      .getMyInfo()
      .pipe(untilDestroyed(this))
      .subscribe(
        user => {
          this.accountService.onSetUserRole(user);
          this.performerId = getActiveBookingPerformerId(user);
          if (!this.performerId) {
            this.loading = false;
            this.loadError = 'Профиль исполнителя для букинга не найден.';
            this.cdr.markForCheck();
            return;
          }
          this.loadApplications();
        },
        () => {
          this.loading = false;
          this.loadError = 'Не удалось загрузить данные аккаунта.';
          this.cdr.markForCheck();
        }
      );
  }

  private loadApplications(): void {
    const id = this.performerId;
    if (!id) {
      return;
    }
    this.loading = true;
    this.loadError = null;
    this.cdr.markForCheck();
    this.bookingService
      .getPerformerApplications(id, {
        page: this.page,
        size: this.pageSize,
        from: this.from || null,
        to: this.to || null,
        status: this.status || null
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        list => {
          this.items = list ?? [];
          this.hasMore = (list?.length ?? 0) >= this.pageSize;
        },
        err => {
          this.items = [];
          this.loadError =
            (err && (err.message || err.error?.message)) || 'Не удалось загрузить заявки.';
        }
      );
  }
}
